import { TileInfo } from "@/components/license-details/tile-info";
import { CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const PORTAL_TILE_BLURBS: Record<string, string> = {
  "Needs Attention":
    "Active registrations 30 to 90 days past renewal, or 30 or more days when that license type’s historical data has been cleaned (OMT not exempt). Properties with a compliance issue are listed separately.",
  "Compliance Issues":
    "Active registrations with an open compliance issue, including Ready for Check.",
  "Balance Due":
    "Registrations with a payment still due. Pay now when you can, or review the properties first.",
  "Bulk Payment":
    "Properties scheduled to be paid later. Open the list to review them, or pay the group when payments are allowed.",
  "Inspection Pending":
    "Inspections still to be completed. Open the property and use Perform Inspection, or scan the QR code to finish it on a phone.",
  "New Registration":
    "Start a registration for a license type available to your organization.",
  Jurisdictions: "Communities in the current filter scope.",
  Ordinances:
    "Live and historic ordinance rows for the jurisdictions in scope.",
  "Active Licenses":
    "Active registrations in the current jurisdiction filters.",
  "Pending Review":
    "Active registrations in the pending-review queue, including unresolved Notify Hera Registry comments.",
  "Unregistered Properties":
    "Properties on file that do not have an active registration.",
  "Inspections Pending Review":
    "Inspections that have been submitted and are waiting for review.",
  "Total Revenue": "Active registrations with a year-to-date payment amount.",
  "HERA Remittance": "Hera's share of year-to-date payments.",
  Refunds: "Active registrations with a partial or full refund.",
  Disputes: "Active registrations with an ongoing payment dispute.",
  "Inactive Licenses":
    "Registrations that are no longer active in the current view.",
  Registrations: "New registrations completed in the selected period.",
  Renewals: "Renewals completed in the selected period.",
  "Registrations + Renewals":
    "New registrations and renewals completed in the selected period.",
  "Dollars received": "Payments received in the selected period.",
  "HERA remittance": "Hera's share of payments in the selected period.",
  "HERA remittance this week":
    "HERA Charge - Retained plus Due to HERA for the current Sunday–Saturday week, including refunds completed this week. Calculated when this page loads, in dollars and cents.",
  "HERA remittance this month":
    "HERA Charge - Retained plus Due to HERA for the current month, including refunds completed this month. Calculated when this page loads, in dollars and cents. Compare the previous month with the remittance report.",
  "Registration compliance":
    "New registrations divided by new cases in the same window.",
  "Renewal on time": "Renewals paid on time divided by renewals due.",
  "Renewed within 30 days":
    "Renewals paid within 30 days divided by renewals due.",
  "Renewed within 90 days":
    "Renewals paid within 90 days divided by renewals due.",
  "Renewed ever": "Renewals paid at any time divided by renewals due.",
  "Total Pipeline": "Open deals on the sales and marketing pipelines.",
  "Contracts Fully Executed":
    "Deals whose contracts are fully executed in the selected period.",
  "Compliant Registered Licenses":
    "Share of active licenses that are compliant. Unregistered properties are not included.",
  "Registrations + Renewals Completed":
    "New registrations and renewals completed in the selected period.",
  "Registrations + Renewals Initiated":
    "New registrations and renewals started in the selected period.",
  "Checks & Invoices > 30 Days":
    "Checks and invoices that have been outstanding for more than 30 days.",
  "Awaiting Checks & Invoices":
    "Check and invoice amounts still outstanding in the current period.",
  "Awaiting ACH Clearance":
    "ACH payments that have not cleared in the current period.",
  "Sales Qualified Leads": "Sales-qualified leads from HubSpot.",
  "Marketing Qualified Leads": "Marketing-qualified leads from HubSpot.",
  "FC Deals": "Open foreclosure-pipeline deals.",
  "LTR Deals": "Open long-term rental pipeline deals.",
  "Total Open Deals": "Open deals across the FC and LTR pipelines.",
  "Qualified Companies Without Deals":
    "Qualified companies that do not yet have a deal.",
  "Latest extract": "Date of the newest ATTOM tax assessor extract.",
  "Extract dates scanned": "How many extract dates were read for this status.",
  "Records (latest)": "Property records in the newest extract.",
  "States in latest": "States present in the newest extract.",
  "Foreclosure cases":
    "Foreclosure case rows indexed by jurisdiction and county.",
};

export function portalTileBlurb(title: string): string | undefined {
  return PORTAL_TILE_BLURBS[title];
}

export function PortalTileTitle({
  title,
  className,
  infoClassName,
}: {
  title: string;
  className?: string;
  infoClassName?: string;
}) {
  const body = PORTAL_TILE_BLURBS[title];
  return (
    <CardTitle className={cn("flex items-center gap-1", className)}>
      <span className="min-w-0">{title}</span>
      {body ? (
        <TileInfo
          label={title}
          title={title}
          body={body}
          className={infoClassName}
        />
      ) : null}
    </CardTitle>
  );
}
